import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { validateEmail, validateName, validatePhone, sanitizeText, checkRateLimit } from '@/lib/security';


interface ConsultationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ConsultationModal = ({ open, onOpenChange }: ConsultationModalProps) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    profession: '',
    age: '',
    industry: '',
    email: '',
    phone: '',
    description: ''
  });
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    
    // Comprehensive validation
    if (!formData.name || !formData.email || !formData.description) {
      toast({
        title: t('consultation.error'),
        description: t('consultation.errorDescription'),
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    // Validate input formats
    if (!validateName(formData.name)) {
      toast({
        title: t('consultation.error'),
        description: "Please enter a valid name (2-50 characters, letters only)",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    if (!validateEmail(formData.email)) {
      toast({
        title: t('consultation.error'),
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    if (formData.phone && !validatePhone(formData.phone)) {
      toast({
        title: t('consultation.error'),
        description: "Please enter a valid phone number",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    // Check rate limiting - more lenient during development
    if (!checkRateLimit('consultation', 10, 3600000)) { // 10 attempts per hour
      toast({
        title: t('consultation.error'),
        description: "Too many consultation requests. Please try again later.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    // Validate privacy policy acceptance
    if (!privacyPolicyAccepted) {
      toast({
        title: t('consultation.error'),
        description: "Please accept the privacy policy to continue.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }


    // Sanitize inputs
    const sanitizedData = {
      name: sanitizeText(formData.name),
      profession: sanitizeText(formData.profession),
      age: sanitizeText(formData.age),
      industry: sanitizeText(formData.industry),
      email: sanitizeText(formData.email),
      phone: sanitizeText(formData.phone),
      description: sanitizeText(formData.description)
    };

    try {
      const response = await fetch('https://obeogkrdszdkvrashtyp.supabase.co/functions/v1/send-consultation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...sanitizedData
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send consultation request');
      }

      toast({
        title: "Success",
        description: "Your consultation request has been sent successfully!",
      });

      setFormData({
        name: '',
        profession: '',
        age: '',
        industry: '',
        email: '',
        phone: '',
        description: ''
      });
      setPrivacyPolicyAccepted(false);
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error sending consultation request:', error);
      toast({
        title: t('consultation.error'),
        description: t('consultation.errorDescription'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl gradient-text">{t('consultation.title')}</DialogTitle>
          <DialogDescription>
            {t('consultation.description')}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">{t('consultation.name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder={t('consultation.namePlaceholder')}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="profession">{t('consultation.profession')}</Label>
              <Input
                id="profession"
                value={formData.profession}
                onChange={(e) => handleInputChange('profession', e.target.value)}
                placeholder={t('consultation.professionPlaceholder')}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="age">{t('consultation.age')}</Label>
              <Input
                id="age"
                type="number"
                value={formData.age}
                onChange={(e) => handleInputChange('age', e.target.value)}
                placeholder={t('consultation.agePlaceholder')}
                min="18"
                max="100"
              />
            </div>
            
            <div>
              <Label htmlFor="industry">{t('consultation.industry')}</Label>
              <Select value={formData.industry} onValueChange={(value) => handleInputChange('industry', value)}>
                <SelectTrigger>
                  <SelectValue placeholder={t('consultation.industryPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="technology">{t('consultation.industryTechnology')}</SelectItem>
                  <SelectItem value="healthcare">{t('consultation.industryHealthcare')}</SelectItem>
                  <SelectItem value="finance">{t('consultation.industryFinance')}</SelectItem>
                  <SelectItem value="education">{t('consultation.industryEducation')}</SelectItem>
                  <SelectItem value="retail">{t('consultation.industryRetail')}</SelectItem>
                  <SelectItem value="manufacturing">{t('consultation.industryManufacturing')}</SelectItem>
                  <SelectItem value="consulting">{t('consultation.industryConsulting')}</SelectItem>
                  <SelectItem value="startup">{t('consultation.industryStartup')}</SelectItem>
                  <SelectItem value="other">{t('consultation.industryOther')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="email">{t('consultation.email')}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder={t('consultation.emailPlaceholder')}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="phone">{t('consultation.phone')}</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder={t('consultation.phonePlaceholder')}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="description">{t('consultation.descriptionLabel')}</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder={t('consultation.descriptionPlaceholder')}
              className="min-h-[100px]"
              required
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="privacy-policy"
              checked={privacyPolicyAccepted}
              onCheckedChange={(checked) => setPrivacyPolicyAccepted(checked as boolean)}
            />
            <Label htmlFor="privacy-policy" className="text-sm">
              {t('consultation.acceptPrivacy')}{' '}
              <a
                href="/commodIT_PrivacyPolicy_hu.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {t('consultation.privacyPolicy')}
              </a>
            </Label>
          </div>


          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              {t('consultation.cancel')}
            </Button>
            <Button type="submit" className="gradient-primary" disabled={isSubmitting}>
              {isSubmitting ? t('consultation.submitting') : t('consultation.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ConsultationModal;